import { normalizeArtifactRef } from 'flare-chat-core/contracts/context/artifact-ref';
import { selectAuthoritativeCanvasTabs } from './artifactBindingProjection.js';

const keys = ['artifact_id', 'artifact_version_id', 'artifact_type', 'content_digest'];
const object = (value) => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const invalidRead = (message) => Object.assign(new TypeError(message), { retryable: false });
const slotType = (tab) => tab.artifact_type ?? tab.metadata?.artifact_ref?.artifact_type;

/** Compose admitted display slots without treating one exact Catalog read as the whole collection. */
export const mergeArtifactTabProjection = (current, incoming) => {
  if (!incoming && current) throw invalidRead('该产物缺少可用的展示绑定');
  if (!current || current === incoming) return incoming ?? null;
  const existing = selectAuthoritativeCanvasTabs(current);
  const additions = selectAuthoritativeCanvasTabs(incoming);
  const types = new Map(existing.map((tab) => [tab.key, slotType(tab)]));
  if (additions.some((tab) => types.has(tab.key) && types.get(tab.key) !== slotType(tab))) {
    throw invalidRead('产物展示槽位类型冲突');
  }
  const slots = new Map([...existing, ...additions].map((tab) => [tab.key, tab]));
  return { ...current, ...incoming, ui_signal: incoming.ui_signal ?? null,
    tabs: [...slots.values()].map((tab) => ({ ...tab, active: tab.key === incoming.active_tab })) };
};

/** Validate a Catalog read as presentation input, never as a replacement session state. */
export const selectArtifactReadProjection = (read, target, projectId) => {
  let ref;
  try { ref = normalizeArtifactRef(read?.artifact_ref); }
  catch { throw invalidRead('产物版本身份无效'); }
  if (ref.scope.project_id !== projectId || target.project_id !== projectId
    || !keys.every((key) => ref[key] === target[key])) throw invalidRead('产物版本与所选目标不一致');
  const body = object(read.body);
  if (body.artifact_id !== ref.artifact_id || body.artifact_type !== ref.artifact_type
    || typeof body.artifact_schema !== 'string' || !body.artifact_schema.trim() || !Number.isSafeInteger(body.schema_version)
    || body.schema_version < 1 || body.content !== object(body.content)) throw invalidRead('产物正文身份无效');
  const publication = read.publication_metadata?.artifact_ref;
  if (read.publication_metadata != null && (!publication || !keys.every((key) => publication[key] === ref[key])
    || ['tenant_id', 'project_id'].some((key) => publication.scope?.[key] !== ref.scope[key]))) {
    throw invalidRead('产物发布信息与所选范围不一致');
  }
  const projection = object(read.artifact_tab_projection);
  const tabs = selectAuthoritativeCanvasTabs(projection);
  const bound = tabs.filter((tab) => tab.key === projection.active_tab && tab.artifact_id === ref.artifact_id
    && (!tab.artifact_type || tab.artifact_type === ref.artifact_type)
    && keys.every((key) => tab.metadata?.artifact_ref?.[key] === ref[key])
    && ['tenant_id', 'project_id'].every((key) => tab.metadata?.artifact_ref?.scope?.[key] === ref.scope[key]));
  if (projection.active_artifact_id !== ref.artifact_id || bound.length !== 1 || bound[0].readiness !== 'ready') {
    throw invalidRead('产物展示绑定与所选版本不一致');
  }
  const row = { ...body.content, artifact_id: ref.artifact_id, artifact_type: ref.artifact_type,
    artifact_schema: body.artifact_schema, schema_version: body.schema_version, artifact_ref: ref,
    artifact_version_id: ref.artifact_version_id, revision_id: ref.artifact_version_id,
    publication_metadata: read.publication_metadata, workspace_capabilities: object(read.workspace_capabilities) };
  return { artifactRef: ref, activeTab: projection.active_tab,
    context: { artifact_tab_projection: projection, artifact_context: [row] } };
};
